import * as React from 'react';
import clsx from 'clsx';
import styles from './ApplicationStatusSteps.module.css';

type Step = {
  id: number;
  label1: string;
  label2: string;
};

type ApplicationStatusStepsProps = {
  activeStep: 1 | 2 | 3 | 4 | 5 | 6 | 0;
  completionPercentage?: number;
  className?: string;
  isSelfServe?: boolean;
};

const STEPS: Step[] = [
  { id: 1, label1: 'Complete', label2: 'Application' },
  { id: 2, label1: 'Reviewing', label2: 'Application' },
  { id: 3, label1: 'Get', label2: 'Approved' },
  { id: 4, label1: 'Sign', label2: 'Lease' },
  { id: 5, label1: 'Move-in', label2: 'New Home' },
];

export const ApplicationStatusSteps: React.FC<ApplicationStatusStepsProps> = ({
  activeStep,
  className,
  completionPercentage,
  isSelfServe,
}) => {
  let curentSteps = STEPS;

  if (isSelfServe) {
    curentSteps = [
      { id: 1, label1: 'Complete', label2: 'Application' },
      { id: 2, label1: 'Reviewing', label2: 'Application' },
      { id: 3, label1: 'Get', label2: 'Approved' },
      { id: 4, label1: 'Sign', label2: 'Lease' },
      { id: 5, label1: 'Belong', label2: 'Countersigns' },
      { id: 6, label1: 'Move-in', label2: 'New Home' },
    ];
  }

  return (
    <>
      <div
        className={clsx(
          'relative mt-sm hidden w-full justify-between md:flex',
          className,
        )}
        role="list"
        aria-label="Application status steps"
      >
        <div className="absolute left-0 top-0 h-[1px] w-full bg-gray" />
        {curentSteps.map((step, index) => {
          const isActive = step.id === activeStep;
          const isLast = index === curentSteps.length - 1;
          const isDone = step.id < activeStep;
          const inUpcoming = step.id > activeStep;

          return (
            <div key={step.id} className="relative">
              <div
                role="listitem"
                className="flex w-[95px] flex-col items-center justify-start px-0 pt-[43px] text-center "
              >
                <div
                  className={clsx('relative flex items-center justify-center')}
                >
                  {!isLast && !isSelfServe && (
                    <div className="md: absolute left-[54px] top-1/2 z-0 flex h-[2px] w-2xl flex-1 -translate-y-1/2 items-center lg:left-[50px] xl:left-[74px]">
                      <div
                        className="w-full border-b border-dashed border-gray"
                        style={{ borderWidth: '1px' }}
                      />
                    </div>
                  )}
                  {!isLast && isSelfServe && (
                    <div className="absolute top-1/2 z-0 flex h-[2px] w-2xl flex-1 -translate-y-1/2 items-center md:left-[42px] lg:left-[37px] xl:left-[55px]">
                      <div
                        className="w-full border-b border-dashed border-gray"
                        style={{ borderWidth: '1px' }}
                      />
                    </div>
                  )}

                  <div
                    className={clsx(
                      'relative flex h-md w-md items-center justify-center rounded-full text-white',
                      isActive && 'bg-blue',
                      isDone && 'bg-green',
                      inUpcoming && 'bg-dark-gray',
                      isActive && styles.pulse,
                    )}
                  >
                    <span className="text-p1 font-semibold">{step.id}</span>
                  </div>
                </div>
                <div className="mt-xs">
                  <p
                    className={clsx(
                      'text-p1',
                      'text-dark-gray',
                      'leading-[1.4]',
                      (isActive || isDone) && 'font-semibold text-navy',
                    )}
                  >
                    {step.label1}
                    <br />
                    {step.label2}
                  </p>

                  {isActive &&
                    activeStep === 1 &&
                    completionPercentage !== undefined && (
                      <div className="whitespace-nowrap  text-p1 font-semibold text-red">
                        {completionPercentage}% Complete
                      </div>
                    )}
                  {isActive && activeStep === 2 && (
                    <div className="mt-2xs whitespace-nowrap text-p1 leading-[1.2] text-dark-gray">
                      1-2 Business Days
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <ApplicationStatusStepsMobile
        activeStep={activeStep}
        className={className}
        completionPercentage={completionPercentage}
      />
    </>
  );
};

const ApplicationStatusStepsMobile: React.FC<ApplicationStatusStepsProps> = ({
  activeStep,
  className,
  completionPercentage,
  isSelfServe,
}) => {
  let curentSteps = STEPS;

  if (isSelfServe) {
    curentSteps = [
      { id: 1, label1: 'Complete', label2: 'Application' },
      { id: 2, label1: 'Reviewing', label2: 'Application' },
      { id: 3, label1: 'Get', label2: 'Approved' },
      { id: 4, label1: 'Sign', label2: 'Lease' },
      { id: 5, label1: 'Belong', label2: 'Countersigns' },
      { id: 6, label1: 'Move-in', label2: 'New Home' },
    ];
  }
  return (
    <div
      className={clsx(
        'relative my-lg flex w-full flex-col gap-lg px-sm py-xl md:hidden ',
        className,
      )}
      role="list"
      aria-label="Application status steps (mobile)"
    >
      <div className="absolute left-1/2 top-0 z-10 h-[1px] w-[90%] -translate-x-1/2 bg-gray" />
      {curentSteps.map((step) => {
        const isActive = step.id === activeStep;
        const isDone = step.id < activeStep;
        const inUpcoming = step.id > activeStep;

        return (
          <div
            key={step.id}
            role="listitem"
            className="flex items-start gap-xs"
          >
            {/* Number circle */}
            <div
              className={clsx(
                'flex shrink-0 translate-y-0 items-center justify-center rounded-full',
                'h-md w-md text-white',
                isActive && 'bg-blue',
                isDone && 'bg-green',
                inUpcoming && 'bg-dark-gray/70',
                isActive && styles.pulse,
              )}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="leading-none text-p1 font-semibold">
                {step.id}
              </span>
            </div>

            <div className="flex-1">
              <div
                className={clsx(
                  'text-p3',
                  'text-dark-gray',
                  (isActive || isDone) && 'font-semibold text-navy',
                )}
              >
                {step.label1}&nbsp;{step.label2}
              </div>

              {isActive &&
                activeStep === 1 &&
                completionPercentage !== undefined && (
                  <div className="whitespace-nowrap  text-p1  font-semibold text-red">
                    {completionPercentage}% Complete
                  </div>
                )}
              {isActive && activeStep === 2 && (
                <div className="whitespace-nowrap text-p1 leading-[1.2] text-dark-gray">
                  1-2 Business Days
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
